/**
 * Mock data generator for Chat Timeline component testing.
 *
 * Provides realistic timeline progression scenarios:
 * - New card entry flow
 * - Saved card flow (skips card entry events)
 * - Payment success/decline/expiry scenarios
 */

import type {
  ChatSessionData,
  TimelineEvent,
  PaymentMethodType,
  PaymentStatus,
} from '../types/chat-timeline.types';
import {
  MOCK_PAYMENT_BASE_URL,
  MOCK_EVENT_INTERVAL_MS,
  MOCK_SESSION_TIMEOUT_MS,
  MOCK_DEFAULT_AMOUNT,
  MOCK_DEFAULT_CURRENCY,
  EVENT_STATUS,
  PAYMENT_STATUS,
} from './constants';

/**
 * Generate mock session data with initial timeline
 */
export const generateMockChatSession = (
  sessionId: string,
  customerId: string,
  amount: number,
  currency: string,
  _scenario: 'new-card' | 'saved-card' | 'apple-pay' | 'google-pay' | 'paypal' | 'decline' | 'expire' = 'new-card'
): ChatSessionData => {
  const now = new Date();
  const paymentLink = `${MOCK_PAYMENT_BASE_URL}${sessionId}`;

  // All scenarios start with the same initial 4 events (all pending except Link created)
  const baseTimeline: TimelineEvent[] = [
    {
      eventType: 'LINK_CREATED',
      status: 'Link created',
      timestamp: now.toISOString(),
      completed: true,
      transactionId: sessionId,
    },
    {
      eventType: 'LINK_OPENED',
      status: 'Link opened',
      timestamp: new Date(now.getTime() + 1000).toISOString(),
      completed: false,
      transactionId: sessionId,
    },
    {
      eventType: 'PAYMENT_METHOD_SELECTED',
      status: 'Payment method selected',
      timestamp: new Date(now.getTime() + 2000).toISOString(),
      completed: false,
      transactionId: sessionId,
    },
    {
      eventType: 'PAYMENT_COMPLETE',
      status: 'Payment complete',
      timestamp: new Date(now.getTime() + 3000).toISOString(),
      completed: false,
      transactionId: sessionId,
    },
  ];

  return {
    sessionId,
    customerId,
    amount,
    currency,
    interactionType: 'CHAT',
    paymentLink,
    timeline: baseTimeline,
    paymentStatus: PAYMENT_STATUS.INITIATED,
    expiresAt: new Date(now.getTime() + MOCK_SESSION_TIMEOUT_MS).toISOString(),
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
  };
};

/**
 * Generate timeline progression events based on scenario
 */
export const generateTimelineProgression = (
  sessionId: string,
  scenario: 'new-card' | 'saved-card' | 'decline' | 'expire' | 'error-retry' | 'error-declined' | 'error-max-attempts' = 'new-card',
  paymentMethod: PaymentMethodType = 'NEW_CARD'
): TimelineEvent[] => {
  const events: TimelineEvent[] = [];
  let timestamp = new Date();

  // Helper to add delay and create timestamp
  const addEvent = (
    eventType: TimelineEvent['eventType'],
    status: string,
    completed: boolean,
    failed = false,
    paymentStatus?: PaymentStatus,
    errorCode?: string,
    errorMessage?: string
  ) => {
    timestamp = new Date(timestamp.getTime() + MOCK_EVENT_INTERVAL_MS);
    events.push({
      eventType,
      status,
      timestamp: timestamp.toISOString(),
      completed,
      failed,
      transactionId: sessionId,
      ...(paymentStatus && { paymentStatus }),
      ...(eventType === 'PAYMENT_METHOD_SELECTED' && { paymentMethod }),
      ...(errorCode && { errorCode }),
      ...(errorMessage && { errorMessage }),
    });
  };

  // LINK_OPENED
  addEvent('LINK_OPENED', EVENT_STATUS.LINK_OPENED, true);

  // PAYMENT_METHOD_SELECTED
  // Voice & Chat only support cards (Visa, Mastercard, Amex) - no Apple Pay, Google Pay, or PayPal
  const methodLabel = paymentMethod === 'SAVED_CARD' ? 'Saved card' : 'New card';
  addEvent('PAYMENT_METHOD_SELECTED', `Payment method selected: ${methodLabel}`, true);

  // For NEW_CARD, add card entry events
  if (paymentMethod === 'NEW_CARD') {
    addEvent('CARDHOLDER_NAME_ENTERED', EVENT_STATUS.CARDHOLDER_NAME_ENTERED, true);
    addEvent('CARD_NUMBER_ENTERED', EVENT_STATUS.CARD_NUMBER_ENTERED, true);
    addEvent('EXPIRY_ENTERED', EVENT_STATUS.EXPIRY_ENTERED, true);
    addEvent('CVC_ENTERED', EVENT_STATUS.CVC_ENTERED, true);
  }

  // PAYMENT_PROCESSING
  addEvent('PAYMENT_PROCESSING', 'Payment processing', true);

  // Final outcome based on scenario
  if (scenario === 'error-retry') {
    // Generic failure - retry-later
    addEvent('PAYMENT_DECLINED', 'Payment failed', false, true, 'FAILED', 'GATEWAY_TIMEOUT', 'Gateway timeout. Please try again.');
  } else if (scenario === 'error-declined') {
    // Terminal bank error - dont-retry
    addEvent('PAYMENT_DECLINED', 'Payment declined', false, true, 'DECLINED', 'CARD_DECLINED', 'Card declined by issuing bank. Please try a different payment method.');
  } else if (scenario === 'error-max-attempts') {
    // Max attempts exceeded
    addEvent('PAYMENT_DECLINED', 'Maximum attempts exceeded', false, true, 'DECLINED', 'MAX_ATTEMPTS_EXCEEDED', 'Maximum payment attempts exceeded. Please create a new payment.');
  } else if (scenario === 'decline') {
    addEvent('PAYMENT_DECLINED', EVENT_STATUS.PAYMENT_FAILED, false, true, PAYMENT_STATUS.DECLINED);
  } else if (scenario === 'expire') {
    addEvent('SESSION_EXPIRED', EVENT_STATUS.SESSION_EXPIRED, false, true, PAYMENT_STATUS.EXPIRED);
  } else {
    addEvent('PAYMENT_COMPLETE', EVENT_STATUS.PAYMENT_COMPLETE, true, false, PAYMENT_STATUS.SETTLED);
  }

  return events;
};

/**
 * Mock timeline progression scenarios for testing
 */
export const mockScenarios = {
  /**
   * New card entry - complete flow
   */
  newCardSuccess: (sessionId: string, customerId: string): ChatSessionData => {
    const session = generateMockChatSession(sessionId, customerId, MOCK_DEFAULT_AMOUNT, MOCK_DEFAULT_CURRENCY, 'new-card');
    return session;
  },

  /**
   * Saved card - skips card entry events
   */
  savedCardSuccess: (sessionId: string, customerId: string): ChatSessionData => {
    const session = generateMockChatSession(sessionId, customerId, MOCK_DEFAULT_AMOUNT, MOCK_DEFAULT_CURRENCY, 'saved-card');
    return session;
  },

  /**
   * Payment declined
   */
  paymentDecline: (sessionId: string, customerId: string): ChatSessionData => {
    const session = generateMockChatSession(sessionId, customerId, MOCK_DEFAULT_AMOUNT, MOCK_DEFAULT_CURRENCY, 'decline');
    return session;
  },

  /**
   * Session expired
   */
  sessionExpire: (sessionId: string, customerId: string): ChatSessionData => {
    const session = generateMockChatSession(sessionId, customerId, MOCK_DEFAULT_AMOUNT, MOCK_DEFAULT_CURRENCY, 'expire');
    return session;
  },

  /**
   * Apple Pay
   */
  applePaySuccess: (sessionId: string, customerId: string): ChatSessionData => {
    const session = generateMockChatSession(sessionId, customerId, MOCK_DEFAULT_AMOUNT, MOCK_DEFAULT_CURRENCY, 'apple-pay');
    return session;
  },

  /**
   * Google Pay
   */
  googlePaySuccess: (sessionId: string, customerId: string): ChatSessionData => {
    const session = generateMockChatSession(sessionId, customerId, MOCK_DEFAULT_AMOUNT, MOCK_DEFAULT_CURRENCY, 'google-pay');
    return session;
  },

  /**
   * PayPal
   */
  paypalSuccess: (sessionId: string, customerId: string): ChatSessionData => {
    const session = generateMockChatSession(sessionId, customerId, MOCK_DEFAULT_AMOUNT, MOCK_DEFAULT_CURRENCY, 'paypal');
    return session;
  },

  /**
   * Error Scenario 1: retry-later (Generic failure, 1st attempt)
   * Temporary gateway timeout - customer should try again
   */
  errorRetryLater: (sessionId: string, customerId: string): ChatSessionData => {
    const now = new Date();
    const paymentLink = `${MOCK_PAYMENT_BASE_URL}${sessionId}`;

    // Start with Link created (completed), all other events pending
    const timeline: TimelineEvent[] = [
      {
        eventType: 'LINK_CREATED',
        status: 'Link created',
        timestamp: now.toISOString(),
        completed: true,
        transactionId: sessionId,
      },
      {
        eventType: 'LINK_OPENED',
        status: 'Link opened',
        timestamp: new Date(now.getTime() + 1000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'PAYMENT_METHOD_SELECTED',
        status: 'Payment method selected: New card',
        timestamp: new Date(now.getTime() + 2000).toISOString(),
        completed: false,
        paymentMethod: 'NEW_CARD',
        transactionId: sessionId,
      },
      {
        eventType: 'CARDHOLDER_NAME_ENTERED',
        status: 'Name on card entered',
        timestamp: new Date(now.getTime() + 3000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'CARD_NUMBER_ENTERED',
        status: 'Card number entered',
        timestamp: new Date(now.getTime() + 4000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'EXPIRY_ENTERED',
        status: 'Expiration date entered',
        timestamp: new Date(now.getTime() + 5000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'CVC_ENTERED',
        status: 'CVC entered',
        timestamp: new Date(now.getTime() + 6000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'PAYMENT_PROCESSING',
        status: 'Payment processing',
        timestamp: new Date(now.getTime() + 7000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'PAYMENT_COMPLETE',
        status: 'Payment complete',
        timestamp: new Date(now.getTime() + 8000).toISOString(),
        completed: false,
        transactionId: sessionId,
        // Error info attached but not visible until progression reaches it
        errorCode: 'GATEWAY_TIMEOUT',
        errorMessage: 'Gateway timeout. Please try again.',
      },
    ];

    return {
      sessionId,
      customerId,
      amount: MOCK_DEFAULT_AMOUNT,
      currency: MOCK_DEFAULT_CURRENCY,
      interactionType: 'CHAT',
      paymentLink,
      timeline,
      paymentStatus: PAYMENT_STATUS.INITIATED,
      expiresAt: new Date(now.getTime() + MOCK_SESSION_TIMEOUT_MS).toISOString(),
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    };
  },

  /**
   * Error Scenario 2: dont-retry (Card declined, 2nd attempt)
   * Terminal bank error - customer should use different card
   */
  errorDontRetry: (sessionId: string, customerId: string): ChatSessionData => {
    const now = new Date();
    const paymentLink = `${MOCK_PAYMENT_BASE_URL}${sessionId}`;

    // Start with Link created (completed), all other events pending
    const timeline: TimelineEvent[] = [
      {
        eventType: 'LINK_CREATED',
        status: 'Link created',
        timestamp: now.toISOString(),
        completed: true,
        transactionId: sessionId,
      },
      {
        eventType: 'LINK_OPENED',
        status: 'Link opened',
        timestamp: new Date(now.getTime() + 1000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'PAYMENT_METHOD_SELECTED',
        status: 'Payment method selected: New card',
        timestamp: new Date(now.getTime() + 2000).toISOString(),
        completed: false,
        paymentMethod: 'NEW_CARD',
        transactionId: sessionId,
      },
      {
        eventType: 'CARDHOLDER_NAME_ENTERED',
        status: 'Name on card entered',
        timestamp: new Date(now.getTime() + 3000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'CARD_NUMBER_ENTERED',
        status: 'Card number entered',
        timestamp: new Date(now.getTime() + 4000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'EXPIRY_ENTERED',
        status: 'Expiration date entered',
        timestamp: new Date(now.getTime() + 5000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'CVC_ENTERED',
        status: 'CVC entered',
        timestamp: new Date(now.getTime() + 6000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'PAYMENT_PROCESSING',
        status: 'Payment processing',
        timestamp: new Date(now.getTime() + 7000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'PAYMENT_COMPLETE',
        status: 'Payment complete',
        timestamp: new Date(now.getTime() + 8000).toISOString(),
        completed: false,
        transactionId: sessionId,
        // Error info attached but not visible until progression reaches it
        errorCode: 'CARD_DECLINED',
        errorMessage: 'Card declined by issuing bank. Please try a different payment method.',
      },
    ];

    return {
      sessionId,
      customerId,
      amount: MOCK_DEFAULT_AMOUNT,
      currency: MOCK_DEFAULT_CURRENCY,
      interactionType: 'CHAT',
      paymentLink,
      timeline,
      paymentStatus: PAYMENT_STATUS.INITIATED,
      expiresAt: new Date(now.getTime() + MOCK_SESSION_TIMEOUT_MS).toISOString(),
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    };
  },

  /**
   * Error Scenario 3: max-attempts (Payment Orchestrator signals max attempts exceeded)
   * Maximum payment attempts exceeded - signaled by backend error code
   */
  errorMaxAttempts: (sessionId: string, customerId: string): ChatSessionData => {
    const now = new Date();
    const paymentLink = `${MOCK_PAYMENT_BASE_URL}${sessionId}`;

    // Start with Link created (completed), all other events pending
    const timeline: TimelineEvent[] = [
      {
        eventType: 'LINK_CREATED',
        status: 'Link created',
        timestamp: now.toISOString(),
        completed: true,
        transactionId: sessionId,
      },
      {
        eventType: 'LINK_OPENED',
        status: 'Link opened',
        timestamp: new Date(now.getTime() + 1000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'PAYMENT_METHOD_SELECTED',
        status: 'Payment method selected: New card',
        timestamp: new Date(now.getTime() + 2000).toISOString(),
        completed: false,
        paymentMethod: 'NEW_CARD',
        transactionId: sessionId,
      },
      {
        eventType: 'CARDHOLDER_NAME_ENTERED',
        status: 'Name on card entered',
        timestamp: new Date(now.getTime() + 3000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'CARD_NUMBER_ENTERED',
        status: 'Card number entered',
        timestamp: new Date(now.getTime() + 4000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'EXPIRY_ENTERED',
        status: 'Expiration date entered',
        timestamp: new Date(now.getTime() + 5000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'CVC_ENTERED',
        status: 'CVC entered',
        timestamp: new Date(now.getTime() + 6000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'PAYMENT_PROCESSING',
        status: 'Payment processing',
        timestamp: new Date(now.getTime() + 7000).toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'PAYMENT_COMPLETE',
        status: 'Payment complete',
        timestamp: new Date(now.getTime() + 8000).toISOString(),
        completed: false,
        transactionId: sessionId,
        // Error info attached but not visible until progression reaches it
        errorCode: 'MAX_ATTEMPTS_EXCEEDED',
        errorMessage: 'Maximum payment attempts exceeded. Please create a new payment.',
      },
    ];

    return {
      sessionId,
      customerId,
      amount: MOCK_DEFAULT_AMOUNT,
      currency: MOCK_DEFAULT_CURRENCY,
      interactionType: 'CHAT',
      paymentLink,
      timeline,
      paymentStatus: PAYMENT_STATUS.INITIATED,
      expiresAt: new Date(now.getTime() + MOCK_SESSION_TIMEOUT_MS).toISOString(),
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    };
  },
};

/**
 * Get next timeline event for mock progression
 */
export const getNextTimelineEvent = (
  currentTimeline: TimelineEvent[],
  scenario: 'new-card' | 'saved-card' | 'decline' | 'expire' | 'error-retry' | 'error-declined' | 'error-max-attempts' = 'new-card',
  paymentMethod: PaymentMethodType = 'NEW_CARD'
): TimelineEvent | null => {
  const allEvents = generateTimelineProgression('mock-session', scenario, paymentMethod);
  const currentEventTypes = currentTimeline.map((e) => e.eventType);

  // Find next event that hasn't occurred yet
  for (const event of allEvents) {
    if (!currentEventTypes.includes(event.eventType)) {
      return {
        ...event,
        timestamp: new Date().toISOString(),
      };
    }
  }

  return null; // No more events
};
